/**
 * HITLGate — Human-in-the-Loop Confirmation Gate
 * Phase 2b — Safety layer for Copilot write/destructive/outward actions.
 *
 * Listens to 'copilot:preview' CustomEvent.
 * Renders as a portal (z-index 9998, below copilot sidebar z-40/9999).
 * Auto-expires after 60 s with countdown.
 * Keyboard: Enter = Confirm, Escape = Cancel.
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle, X, Edit3, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  type ActionClass,
  type ActionGuardResult,
  setSessionAllow,
} from './action-guard'
import type { UiAction } from './action-bridge'
import type { BackendToolCall } from './action-guard'

// ── CustomEvent payload ────────────────────────────────────────────────────────

export interface CopilotPreviewPayload {
  /** Unique request id — de-dupe repeated events */
  requestId: string
  actions: Array<UiAction | BackendToolCall>
  guardResult: ActionGuardResult
  /** Current form field values (for diff display) */
  currentValues?: Record<string, unknown>
  /** Proposed form field values */
  proposedValues?: Record<string, unknown>
  /** Called when user confirms */
  onConfirm: (editedValues?: Record<string, unknown>) => void
  /** Called when user cancels */
  onCancel: () => void
}

declare global {
  interface WindowEventMap {
    'copilot:preview': CustomEvent<CopilotPreviewPayload>
  }
}

// ── Human-readable action description ─────────────────────────────────────────

function describeAction(action: UiAction | BackendToolCall): string {
  const op = action.op
  const a = action as Record<string, unknown>

  switch (op) {
    case 'fillForm':
      return `Remplir le formulaire "${a.formId ?? a.form_id ?? 'inconnu'}" avec ${Object.keys((a.values as Record<string,unknown>) ?? {}).length} champ(s)`
    case 'setField':
      return `Modifier le champ "${a.field ?? a.field_name}" → "${a.value}"`
    case 'submitForm':
      return `Soumettre le formulaire "${a.formId ?? a.form_id ?? 'inconnu'}"`
    case 'deleteRecord':
      return `Supprimer l'enregistrement "${a.recordId ?? a.id ?? ''}"`
    case 'publishContent':
      return `Publier le contenu "${a.contentId ?? a.title ?? ''}"`
    case 'sendEmail':
      return `Envoyer un e-mail à "${a.to ?? 'destinataire inconnu'}"`
    case 'submitToExternalSystem':
      return `Soumettre au système externe "${a.system ?? 'inconnu'}"`
    case 'navigate':
      return `Naviguer vers "${a.to}"`
    default:
      return `Exécuter l'action "${op}"`
  }
}

// ── Diff table component ───────────────────────────────────────────────────────

function FieldDiffTable({
  current,
  proposed,
  editable,
  onEdit,
}: {
  current: Record<string, unknown>
  proposed: Record<string, unknown>
  editable: boolean
  onEdit: (field: string, value: string) => void
}) {
  const allFields = Array.from(
    new Set([...Object.keys(current), ...Object.keys(proposed)]),
  )

  return (
    <table className="w-full text-xs border-collapse mt-2">
      <thead>
        <tr className="text-muted-foreground">
          <th className="text-left py-1 pr-2 font-medium w-1/3">Champ</th>
          <th className="text-left py-1 pr-2 font-medium w-1/3">Actuel</th>
          <th className="text-left py-1 font-medium w-1/3">Proposé</th>
        </tr>
      </thead>
      <tbody>
        {allFields.map((field) => {
          const cur = String(current[field] ?? '—')
          const prop = String(proposed[field] ?? '—')
          const changed = cur !== prop

          return (
            <tr key={field} className={cn('border-t border-border/40', changed && 'bg-amber-50/50 dark:bg-amber-950/20')}>
              <td className="py-1 pr-2 font-mono text-muted-foreground truncate max-w-[80px]">
                {field}
              </td>
              <td className="py-1 pr-2 text-muted-foreground/70 truncate max-w-[80px]">
                {cur}
              </td>
              <td className="py-1">
                {editable ? (
                  <input
                    className="w-full border border-border rounded px-1 py-0.5 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    defaultValue={prop === '—' ? '' : prop}
                    onChange={(e) => onEdit(field, e.target.value)}
                  />
                ) : (
                  <span className={cn(changed && 'font-medium text-primary')}>
                    {prop}
                  </span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// ── HITLGate Component ─────────────────────────────────────────────────────────

const AUTO_EXPIRE_SECONDS = 60

export function HITLGate() {
  const [payload, setPayload] = useState<CopilotPreviewPayload | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [editedValues, setEditedValues] = useState<Record<string, unknown>>({})
  const [alwaysAllow, setAlwaysAllow] = useState(false)
  const [countdown, setCountdown] = useState(AUTO_EXPIRE_SECONDS)
  const seenIds = useRef<Set<string>>(new Set())
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ── Listen for copilot:preview events ──────────────────────────────────────
  useEffect(() => {
    function onPreview(e: CustomEvent<CopilotPreviewPayload>) {
      const p = e.detail
      if (seenIds.current.has(p.requestId)) return
      seenIds.current.add(p.requestId)

      setPayload(p)
      setEditMode(false)
      setEditedValues(p.proposedValues ? { ...p.proposedValues } : {})
      setAlwaysAllow(false)
      setCountdown(AUTO_EXPIRE_SECONDS)
    }

    window.addEventListener('copilot:preview', onPreview)
    return () => window.removeEventListener('copilot:preview', onPreview)
  }, [])

  // ── Countdown / auto-expire ─────────────────────────────────────────────────
  useEffect(() => {
    if (!payload) {
      if (countdownRef.current) clearInterval(countdownRef.current)
      return
    }

    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          handleCancel()
          return 0
        }
        return c - 1
      })
    }, 1000)

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload])

  // ── Keyboard handler ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!payload) return

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter' && !editMode) {
        e.preventDefault()
        handleConfirm()
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, editMode, editedValues, alwaysAllow])

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleConfirm = useCallback(() => {
    if (!payload) return

    if (alwaysAllow && payload.guardResult.actionClass) {
      setSessionAllow(payload.guardResult.actionClass as ActionClass, true)
    }

    payload.onConfirm(editMode ? editedValues : undefined)
    setPayload(null)
  }, [payload, alwaysAllow, editMode, editedValues])

  const handleCancel = useCallback(() => {
    if (!payload) return
    payload.onCancel()
    setPayload(null)
  }, [payload])

  const handleEdit = useCallback((field: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }))
  }, [])

  // ── Nothing to show ─────────────────────────────────────────────────────────
  if (!payload) return null

  const { actions, guardResult, currentValues, proposedValues } = payload
  const isDestructiveOrOutward =
    guardResult.actionClass === 'destructive' ||
    guardResult.actionClass === 'outward'

  const hasDiff =
    (currentValues && Object.keys(currentValues).length > 0) ||
    (proposedValues && Object.keys(proposedValues).length > 0)

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Confirmation d'action Copilot"
      // #FF-hitl — marker so a create <DialogContent> can prevent its Radix
      // dismiss-on-outside when the user interacts with THIS gate (the gate is a
      // sibling portal, so a click inside it counts as "outside" the dialog and
      // would otherwise close the underlying create form before ✨-fill applies).
      data-hitl-gate=""
      className="fixed inset-0 z-[9998] flex items-end justify-end p-6 pointer-events-none"
    >
      {/* Backdrop — subtle, doesn't block scroll */}
      <div
        className="absolute inset-0 bg-black/10 pointer-events-auto"
        onClick={handleCancel}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className={cn(
          'relative pointer-events-auto w-full max-w-sm rounded-xl border shadow-2xl bg-background',
          'animate-in slide-in-from-bottom-4 duration-200',
          // margin-right to avoid overlapping the copilot sidebar (≈400px wide, z-50)
          'mr-[420px]',
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            {isDestructiveOrOutward ? (
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
            ) : (
              <CheckCircle className="h-4 w-4 text-primary shrink-0" />
            )}
            <span className="text-sm font-semibold">
              L&apos;assistant veut effectuer ces actions — confirmer&nbsp;?
            </span>
          </div>
          <button
            onClick={handleCancel}
            className="text-muted-foreground hover:text-foreground ml-2 shrink-0"
            aria-label="Annuler"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Destructive/outward warning banner */}
        {isDestructiveOrOutward && (
          <div className="mx-4 mb-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3 shrink-0" />
            <span>
              <strong>Action irréversible</strong>{guardResult.reason ? ` — ${guardResult.reason}` : ' — êtes-vous certain ?'}
            </span>
          </div>
        )}

        {/* PII notice */}
        {guardResult.piiTouched && (
          <div className="mx-4 mb-2 rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700 px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
            Ce formulaire touche des données personnelles (PII / Loi 25 / RGPD).
          </div>
        )}

        {/* Action list */}
        <div className="px-4 pb-2 space-y-1">
          {actions.map((action, idx) => (
            <div
              key={idx}
              className="flex items-start gap-1.5 text-xs text-muted-foreground"
            >
              <span className="shrink-0 text-primary font-medium">{idx + 1}.</span>
              <span>{describeAction(action)}</span>
            </div>
          ))}
        </div>

        {/* Diff table (fillForm / setField) */}
        {hasDiff && (
          <div className="px-4 pb-2">
            <FieldDiffTable
              current={currentValues ?? {}}
              proposed={editMode ? editedValues : (proposedValues ?? {})}
              editable={editMode}
              onEdit={handleEdit}
            />
          </div>
        )}

        {/* Always-allow checkbox */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <input
            id="hitl-always-allow"
            type="checkbox"
            checked={alwaysAllow}
            onChange={(e) => setAlwaysAllow(e.target.checked)}
            className="rounded border-border h-3.5 w-3.5 accent-primary cursor-pointer"
          />
          <label
            htmlFor="hitl-always-allow"
            className="text-xs text-muted-foreground cursor-pointer select-none"
          >
            Toujours autoriser ce type d&apos;action pour cette session
          </label>
        </div>

        {/* Footer — buttons + countdown */}
        <div className="border-t px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 text-xs text-muted-foreground/60">
            <Clock className="h-3 w-3" />
            <span>Expire dans {countdown}s</span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleCancel}
            >
              Annuler
            </Button>

            {hasDiff && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setEditMode((v) => !v)}
              >
                <Edit3 className="h-3 w-3" />
                {editMode ? 'Vue aperçu' : 'Modifier'}
              </Button>
            )}

            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={handleConfirm}
              autoFocus
            >
              Confirmer
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default HITLGate
